import React from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Plus, Search, FileText, Layers, ClipboardList, CheckCircle2, ArrowRight, Activity } from "lucide-react";
import useApi from "../../hooks/useApi";
import { scoreColor } from "../../lib/sales";

const STATUS_META = {
  draft: { label: "Draft", color: "#8A8C82", bg: "rgba(138,140,130,.14)" },
  analysed: { label: "Analysed", color: "#2F6BFF", bg: "rgba(47,107,255,.10)" },
  reviewed: { label: "Reviewed", color: "#B8892B", bg: "rgba(184,137,43,.14)" },
  completed: { label: "Completed", color: "#5F8213", bg: "rgba(147,192,32,.14)" },
};

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

function CheckRow({ item }) {
  const navigate = useNavigate();
  const s = STATUS_META[item.status] || STATUS_META.draft;
  const dest = item.status === "completed" ? `/sales/check/${item.id}/report` : `/sales/check/${item.id}`;
  const sc = item.overall_score != null ? scoreColor(item.overall_score) : null;
  return (
    <button onClick={() => navigate(dest)} data-testid={`hc-row-${item.id}`}
      className="w-full text-left card card-hover p-4 flex items-center gap-4">
      <span className="grid place-items-center w-11 h-11 rounded-xl text-sm font-bold text-white shrink-0" style={{ background: "var(--grad-ink)" }}>{initials(item.prospect?.company)}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-ink truncate">{item.prospect?.company || "Untitled prospect"}</span>
          {item.is_nichecom_customer && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5" style={{ background: "rgba(147,192,32,.14)", color: "#5F8213" }}>NicheCom</span>}
        </div>
        <div className="text-sm text-ink-2 truncate">{item.prospect?.branch}{item.prospect?.postcode ? ` · ${item.prospect.postcode}` : ""}</div>
      </div>
      {sc && (
        <div className="text-center shrink-0">
          <div className="text-lg font-extrabold tabular-nums" style={{ color: sc.fg }}>{item.overall_score}</div>
          <div className="text-[10px] text-ink-3 uppercase tracking-wider">Score</div>
        </div>
      )}
      {item.potential != null && (
        <div className="text-center shrink-0 hidden sm:block">
          <div className="text-lg font-extrabold tabular-nums" style={{ color: "#5F8213" }}>{item.potential}</div>
          <div className="text-[10px] text-ink-3 uppercase tracking-wider">Potential</div>
        </div>
      )}
      <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 shrink-0" style={{ color: s.color, background: s.bg }}>{s.label}</span>
      <ArrowRight size={16} className="text-ink-3 shrink-0" />
    </button>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card p-5">
      <Icon size={18} strokeWidth={1.75} className="text-ink-3" />
      <div className="text-3xl font-extrabold tracking-tight text-ink mt-3 tabular-nums">{value}</div>
      <div className="label-caps mt-1">{label}</div>
    </div>
  );
}

export default function SalesHome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const filter = params.get("filter");
  const { data, loading } = useApi("/sales/health-checks");

  const counts = data?.counts || { total: 0, drafts: 0, analysed: 0, completed: 0 };
  let items = data?.items || [];
  let heading = "Recent Health Checks";
  if (filter === "drafts") { items = data?.drafts || []; heading = "Draft Analyses"; }
  else if (filter === "completed") { items = data?.completed || []; heading = "Completed Reports"; }

  return (
    <div>
      <header className="sticky top-0 z-20 flex items-center gap-4 px-6 sm:px-8 py-4 border-b backdrop-blur-md" style={{ background: "rgba(247,248,246,.85)", borderColor: "#E7E8E4" }}>
        <div className="min-w-0">
          <h1 className="text-lg font-bold tracking-tight text-ink">Sales Director</h1>
          <p className="text-sm text-ink-2">Social Health Check — prospecting diagnostics</p>
        </div>
        <button onClick={() => navigate("/sales/new")} data-testid="sales-home-new" className="ml-auto btn-primary focus-ring">
          <Plus size={16} strokeWidth={2.25} /> New Social Health Check
        </button>
      </header>

      <div className="px-6 sm:px-8 py-8 max-w-6xl">
        {/* Hero */}
        <div className="rounded-2xl p-8 mb-8 relative overflow-hidden" style={{ background: "var(--grad-ink)" }}>
          <div className="blob" style={{ width: 460, height: 460, top: -180, right: -120, background: "radial-gradient(circle, rgba(147,192,32,.3), transparent 70%)" }} />
          <div className="relative z-10 max-w-xl">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}><Activity size={14} /> Sales diagnostic</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-white mt-3">Turn any branch into an evidence-led opportunity.</h2>
            <p className="text-sm mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,.66)" }}>Analyse a prospect's social presence, reveal a defensible Social Health Score, and show them exactly how much growth is on the table — every number traceable to its source.</p>
            <button onClick={() => navigate("/sales/new")} data-testid="sales-hero-new" className="btn-primary focus-ring mt-6">
              <Plus size={16} strokeWidth={2.25} /> Start a Health Check
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Layers} label="Total prospects" value={counts.total} />
          <Stat icon={ClipboardList} label="Draft analyses" value={counts.drafts} />
          <Stat icon={FileText} label="Analysed" value={counts.analysed} />
          <Stat icon={CheckCircle2} label="Completed reports" value={counts.completed} />
        </div>

        {/* List */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-ink">{heading}</h3>
          {filter && <Link to="/sales" className="text-sm text-ink-2 hover:text-ink">View all</Link>}
        </div>

        {loading ? (
          <div className="card p-10 text-center text-ink-3">Loading…</div>
        ) : items.length === 0 ? (
          <div className="card p-12 text-center">
            <Search size={26} className="mx-auto text-ink-3" />
            <p className="text-ink font-semibold mt-3">No health checks yet</p>
            <p className="text-sm text-ink-2 mt-1">Create your first Social Health Check to get started.</p>
            <button onClick={() => navigate("/sales/new")} className="btn-primary focus-ring mt-5 mx-auto"><Plus size={16} strokeWidth={2.25} /> New Social Health Check</button>
          </div>
        ) : (
          <div className="space-y-2.5">{items.map((i) => <CheckRow key={i.id} item={i} />)}</div>
        )}
      </div>
    </div>
  );
}
